import { useQuery } from '@tanstack/react-query';
import { useApiClients } from '../ApiClientsProvider';
import { getMaxExpiryForRange } from '@oai-frontend-monorepo/utils';
import { ensureMarketHours } from './useMarketHours';
import { ensureOptionContract } from './useOptionContracts';
import { queryClient } from '../query-client';
import OaiApiClient from '../clients/oai-api/oai-api-client';

type Params = {
  symbol?: string;
  range?: number;
  maxStrikeDistance?: number;
};

export const ensureChainDetails = async ({ symbol, range }: Params) => {
  return await queryClient.ensureQueryData({
    queryKey: ['chain-details', symbol, range],

    queryFn: async () => {
      if (!symbol || !range) {
        throw new Error('Symbol is required');
      }

      const [marketHours, { last }] = await Promise.all([
        ensureMarketHours(),
        ensureOptionContract({ symbol }),
      ]);

      return OaiApiClient.getChainDetails({
        symbol: symbol,
        maxExpire: getMaxExpiryForRange(range, marketHours),
        atm: last,
      });
    },
  });
};

export const prefetchChainDetails = async ({ symbol, range }: Params) => {
  return await queryClient.prefetchQuery({
    queryKey: ['chain-details', symbol, range],
    queryFn: async () => {
      if (!symbol || !range) {
        throw new Error('Symbol is required');
      }

      const [marketHours, { last }] = await Promise.all([
        ensureMarketHours(),
        ensureOptionContract({ symbol }),
      ]);

      return OaiApiClient.getChainDetails({
        symbol: symbol,
        maxExpire: getMaxExpiryForRange(range, marketHours),
        atm: last,
      });
    },
  });
};

export const useChainDetails = ({
  symbol,
  range,
  maxStrikeDistance,
}: Params) => {
  const { oaiApi } = useApiClients();

  return useQuery({
    queryKey: ['chain-details', symbol, range],
    queryFn: async () => {
      if (!symbol || !range) {
        throw new Error('Symbol is required');
      }

      const [marketHours, { last }] = await Promise.all([
        ensureMarketHours(),
        ensureOptionContract({ symbol }),
      ]);

      return oaiApi.getChainDetails({
        symbol: symbol,
        maxExpire: getMaxExpiryForRange(range, marketHours),
        atm: last,
        maxStrikeDistance,
      });
    },
    staleTime: 1000 * 60,
    enabled: !!symbol && !!range,
  });
};
